// ApproverSelectionPopup.tsx - Updated with GatePassForm Design System
import React, { useState, useEffect, useCallback } from 'react';
import './ApproverSelectionPopup.css';
import '../companyManagement/CompanyFormOverrides.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface User {
  userId: number;
  userName: string;
  userEmail: string;
}

interface FunctionWithApproversReviewers {
  functionId: number;
  functionName: string;
  functionDescription: string;
  companyId: number;
  companyName: string;
  approvers: User[];
  reviewers: User[];
}

interface ApproverSelection {
  function: FunctionWithApproversReviewers | null;
  selectedApprovers: User[];
}

interface StatusMessage {
  type: 'success' | 'error' | null;
  message: string;
}

type StepType = 'function' | 'users';

interface ApproverSelectionPopupProps {
  isOpen: boolean;
  currentSelection: ApproverSelection | null;
  userCompanyId?: number;
  getAccessToken: () => Promise<string>;
  onClose: () => void;
  onConfirm: (selection: ApproverSelection) => void;
  onStatusUpdate: (status: StatusMessage) => void;
}

// SVG Icons for empty states
const FunctionIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    width="48" 
    height="48" 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M20 8H28C29.1046 8 30 8.89543 30 10V12H34C35.1046 12 36 12.8954 36 14V36C36 37.1046 35.1046 38 34 38H14C12.8954 38 12 37.1046 12 36V14C12 12.8954 12.8954 12 14 12H18V10C18 8.89543 18.8954 8 20 8ZM20 12H28V10H20V12ZM24 18C25.1046 18 26 18.8954 26 20C26 21.1046 25.1046 22 24 22C22.8954 22 22 21.1046 22 20C22 18.8954 22.8954 18 24 18ZM20 26H28V24H20V26ZM20 30H28V28H20V30Z" 
      fill="currentColor"
      opacity="0.4"
    />
  </svg>
);

const ApproverIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className} 
    width="48" 
    height="48" 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M24 4C28.4183 4 32 7.58172 32 12C32 16.4183 28.4183 20 24 20C19.5817 20 16 16.4183 16 12C16 7.58172 19.5817 4 24 4ZM24 24C30.6274 24 36 29.3726 36 36V40C36 42.2091 34.2091 44 32 44H16C13.7909 44 12 42.2091 12 40V36C12 29.3726 17.3726 24 24 24Z" 
      fill="currentColor"
      opacity="0.4"
    />
    <path 
      d="M34 18L36 20L42 14" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      opacity="0.6"
    />
  </svg>
);

export const ApproverSelectionPopup: React.FC<ApproverSelectionPopupProps> = ({
  isOpen,
  currentSelection,
  userCompanyId,
  getAccessToken,
  onClose,
  onConfirm,
  onStatusUpdate
}) => {
  const [step, setStep] = useState<StepType>('function');
  const [showHelp, setShowHelp] = useState(true);
  const [functions, setFunctions] = useState<FunctionWithApproversReviewers[]>([]);
  const [selectedFunction, setSelectedFunction] = useState<FunctionWithApproversReviewers | null>(null);
  const [selectedApprovers, setSelectedApprovers] = useState<User[]>([]);
  
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false);
  const [functionSearchTerm, setFunctionSearchTerm] = useState('');

  // Initialize with current selection when popup opens
  useEffect(() => {
    if (isOpen && currentSelection) {
      setSelectedFunction(currentSelection.function);
      setSelectedApprovers(currentSelection.selectedApprovers || []);
      
      if (currentSelection.function) {
        setStep('users' as StepType);
      } else {
        setStep('function' as StepType);
      }
    } else if (isOpen) {
      // Reset state when opening fresh
      setStep('function' as StepType);
      setSelectedFunction(null);
      setSelectedApprovers([]);
      setFunctionSearchTerm('');
    }
  }, [isOpen, currentSelection]);

  // Lock body scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const loadFunctions = useCallback(async () => {
    if (!userCompanyId) {
      onStatusUpdate({
        type: 'error',
        message: 'User company ID not available. Please contact support.'
      });
      return;
    }

    setIsLoadingFunctions(true);
    
    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/company-functions/${userCompanyId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFunctions(data.data);
        if (data.data.length === 0) {
          onStatusUpdate({
            type: 'error',
            message: 'No divisions found for your company. Please contact the administrator.'
          });
        }
      } else {
        onStatusUpdate({
          type: 'error',
          message: data.message || 'Failed to load divisions'
        });
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
      onStatusUpdate({
        type: 'error',
        message: 'Failed to load divisions. Please try again.'
      });
    } finally {
      setIsLoadingFunctions(false);
    }
  }, [userCompanyId, getAccessToken, onStatusUpdate]);

  // Load functions when popup opens
  useEffect(() => {
    if (isOpen && step === 'function') {
      loadFunctions();
    }
  }, [isOpen, step, loadFunctions]);

  const handleFunctionSelect = (func: FunctionWithApproversReviewers) => {
    setSelectedFunction(func);
    setSelectedApprovers([]);
    setStep('users' as StepType);
  };

  const handleApproverToggle = (approver: User) => {
    setSelectedApprovers(prev => {
      const isSelected = prev.some(a => a.userId === approver.userId);
      if (isSelected) {
        return prev.filter(a => a.userId !== approver.userId);
      } else {
        return [...prev, approver];
      }
    });
  };

  const handleConfirm = () => {
    if (!selectedFunction) {
      onStatusUpdate({
        type: 'error',
        message: 'Please select a division.'
      });
      return;
    }

    if (selectedApprovers.length === 0) {
      onStatusUpdate({
        type: 'error',
        message: 'Please select at least one approver.'
      });
      return;
    }

    const selection: ApproverSelection = {
      function: selectedFunction,
      selectedApprovers
    };

    onConfirm(selection);
  };

  const filteredFunctions = functions.filter(func =>
    func.functionName.toLowerCase().includes(functionSearchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-container gatepass-style" onClick={(e) => e.stopPropagation()}>
        <div className="popup-header">
          <div className="popup-title-section">
            <span className="popup-title-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </span>
            <h3>Select Approvers</h3>
          </div>
          <button onClick={onClose} className="popup-close-btn" title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

      <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="popup-content approver-popup-inner">
          {/* Step Indicator - GatePass Style */}
          <div className="progress-indicator">
            <div className="progress-steps">
              <div className="progress-step">
                <div className={`step-circle ${step === 'function' ? 'current' : step === 'users' ? 'completed' : 'pending'}`}>
                  1
                </div>
                <div className={`step-connector ${step === 'users' ? 'completed' : 'pending'}`} />
              </div>
              <div className="progress-step">
                <div className={`step-circle ${step === 'users' ? 'current' : 'pending'}`}>
                  2
                </div>
              </div>
            </div>
            <div className="progress-info">
              <h3>
                {step === 'function' ? 'Select Division' : 'Choose Approvers'}
              </h3>
              <p>
                {step === 'function' 
                  ? 'Choose the divions that can approve your request' 
                  : 'Select one or more approvers for this division'
                }
              </p>
            </div>
          </div>

          {/* Step 1: Function Selection */}
          {step === 'function' && (
            <div className="step-content">
              <div className="selection-section">
                <div className="selection-header">
                  <h3>
                    Available Divisions
                    <span className="selection-count-badge">
                      {filteredFunctions.length} found
                    </span>
                  </h3>
                </div>

                <div className="search-container">
                  <div className="search-input-wrapper">
                    <svg className="search-icon-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      type="text"
                      placeholder="Search divisions..."
                      value={functionSearchTerm}
                      onChange={(e) => setFunctionSearchTerm(e.target.value)}
                      className="field-input"
                    />
                  </div>
                </div>

                {isLoadingFunctions ? (
                  <div className="selection-empty">
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Loading divisions...</p>
                    </div>
                  </div>
                ) : filteredFunctions.length === 0 ? (
                  <div className="selection-empty">
                    <FunctionIcon className="empty-icon" />
                    <p className="empty-message">No divisions found</p>
                    <p className="empty-description">
                      No divisions are configured for your company
                    </p>
                  </div>
                ) : (
                    <div className="selected-items-table approver-popup-table">
                    <table className="selection-table">
                      <thead>
                        <tr>
                          <th>Division Name</th>
                          <th>Approvers</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFunctions.map((func) => (
                          <tr 
                            key={func.functionId}
                            className={selectedFunction?.functionId === func.functionId ? 'selected' : ''}
                            onClick={() => handleFunctionSelect(func)}
                          >
                            <td className="function-name">
                              <strong>{func.functionName}</strong>
                            </td>
                            <td className="function-approvers">
  {func.approvers.length > 0 ? (
    <span className="approver-count-badge">
      {func.approvers.length}
    </span>
  ) : (
    <span className="approver-count-badge no-approvers">
      0
    </span>
  )}
</td>
                            <td className="function-description">
                              {func.functionDescription || 'No description'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Approver Selection */}
          {step === 'users' && selectedFunction && (
            <div className="step-content">
              <div className="step-header">
                <button
                  onClick={() => setStep('function' as StepType)}
                  className="prev-btn"
                >
                  ← Back to Divisions
                </button>
                <h4>{selectedFunction.functionName}</h4>
              </div>

              <div className="selection-section">
                <div className="selection-header">
                  <h3>
                    Select Approvers
                    <span className="selection-count-badge required">
                      {selectedApprovers.length} selected
                    </span>
                  </h3>
                </div>

                {selectedFunction.approvers.length === 0 ? (
                  <div className="selection-empty">
                    <ApproverIcon className="empty-icon" />
                    <p className="empty-message">No approvers configured</p>
                    <p className="empty-description">
                      This division has no approvers. Please contact the administrator.
                    </p>
                  </div>
                ) : (
                  <div className="selected-items-table">
                    <table className="selection-table">
                      <thead>
                        <tr>
                          <th>Approver Name</th>
                          <th>Email</th>
                          <th>Select</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedFunction.approvers.map((approver) => {
                          const isSelected = selectedApprovers.some(a => a.userId === approver.userId);
                          return (
                            <tr 
                              key={approver.userId}
                              className={isSelected ? 'selected' : ''}
                              onClick={() => handleApproverToggle(approver)}
                            >
                              <td className="approver-name">
                                <strong>{approver.userName}</strong>
                                
                              </td>
                              <td className="approver-email">
                                {approver.userEmail}
                              </td>
                              <td className="approver-selection">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleApproverToggle(approver)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="selection-checkbox"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {selectedApprovers.length > 0 && (
                  <div className="form-success">
                    <span className="success-icon" style={{ fontSize: '14px', color: '#6b7280' }}>✓</span>
                    <span className="success-text">
                      <strong>{selectedApprovers.length} approver(s) selected:</strong> {selectedApprovers.map(a => a.userName).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="popup-footer">
          <div className="footer-info">
            {step === 'users' && selectedApprovers.length > 0 && (
              <span className="selection-count has-selection">
                {selectedApprovers.length} approver{selectedApprovers.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="footer-actions">
            <button onClick={onClose} className="cancel-btn">Cancel</button>
            {step === 'users' && (
              <button
                onClick={handleConfirm}
                className="next-btn"
                disabled={selectedApprovers.length === 0}
              >
                Confirm Approvers
              </button>
            )}
          </div>
        </div>
        </div>{/* end left column */}

          {/* Help Panel — animated */}
          <div style={{
            width: showHelp ? 280 : 38,
            minWidth: showHelp ? 280 : 38,
            background: '#fafbfc',
            borderLeft: '1px solid #e5e7eb',
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease, min-width 0.3s ease'
          }}>
            <div
              onClick={() => setShowHelp(!showHelp)}
              title={showHelp ? 'Hide Guide' : 'Show Guide'}
              style={{
                position: 'absolute', top: 0, right: 0, bottom: 0, width: 32,
                background: '#f3f4f6', borderLeft: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'flex-start', paddingTop: 14, gap: 6,
                cursor: 'pointer', color: '#9ca3af', zIndex: 5
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {showHelp
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
              <span style={{
                writingMode: 'vertical-rl', textOrientation: 'mixed',
                fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
                whiteSpace: 'nowrap', fontFamily: "'Segoe UI', sans-serif"
              }}>{showHelp ? 'Hide Guide' : 'Show Guide'}</span>
            </div>
            <div style={{
              padding: '20px 50px 20px 18px',
              overflowY: 'auto',
              boxSizing: 'border-box' as const,
              minWidth: 280,
              opacity: showHelp ? 1 : 0,
              transition: 'opacity 0.2s ease'
            }}>
              <div className="help-panel-card help-active">
                <h4 className="help-card-title">Select Approvers</h4>
                <p className="help-card-description">Start by choosing the division related to your work, then select the approvers who will review and approve this gate pass request. At least one approver is required.</p>
                <div className="help-card-tip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <span>Step 1: Pick the division relevant to your request. Step 2: Select one or more approvers from that division.</span>
                </div>
              </div>
              <div className="help-panel-quickref">
                <h4 className="help-quickref-title">Quick Reference</h4>
                <ul className="help-quickref-list">
                  <li><span className="help-quickref-bullet"></span><span>Step 1: Choose the division related to your work</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Step 2: Select one or more approvers</span></li>
                  <li><span className="help-quickref-bullet"></span><span>Approvers receive task notifications on submission</span></li>
                  <li><span className="help-quickref-bullet"></span><span>You can change selections before confirming</span></li>
                </ul>
              </div>
            </div>
          </div>
      </div>{/* end flex row */}
      </div>
    </div>
  );
};